import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";
import { TrackBadge } from "@/components/TrackBadge";
import { ProvenancePill } from "@/components/Provenance";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Mail, FileText, Check, X, ShieldAlert, ExternalLink } from "lucide-react";

export default function Approvals() {
  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="approvals-view">
      <header>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Approval Queues</h1>
        <p className="text-sm text-muted-foreground mt-1">Outreach and submission are approved separately, each behind an explicit confirmation.</p>
      </header>
      <Tabs defaultValue="outreach">
        <TabsList data-testid="approval-tabs">
          <TabsTrigger value="outreach" data-testid="approval-tab-outreach" className="gap-1.5"><Mail className="h-4 w-4" /> Outreach</TabsTrigger>
          <TabsTrigger value="submission" data-testid="approval-tab-submission" className="gap-1.5"><FileText className="h-4 w-4" /> Submission</TabsTrigger>
        </TabsList>
        <TabsContent value="outreach" className="pt-5"><Queue kind="outreach" /></TabsContent>
        <TabsContent value="submission" className="pt-5"><Queue kind="submission" /></TabsContent>
      </Tabs>
    </div>
  );
}

function Queue({ kind }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [approve, setApprove] = useState(null);
  const { data: items = [], isLoading } = useQuery({ queryKey: [`queue-${kind}`], queryFn: async () => (await api.get(`/approvals/queue?kind=${kind}`)).data });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: [`queue-${kind}`] });
    qc.invalidateQueries({ queryKey: ["q-outreach-nav"] });
    qc.invalidateQueries({ queryKey: ["q-submission-nav"] });
  };

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (items.length === 0) return (
    <div className="rounded-xl border border-dashed border-border py-16 text-center">
      <Check className="h-8 w-8 mx-auto text-emerald-400 mb-2" />
      <p className="text-sm text-muted-foreground">No {kind} drafts awaiting approval.</p>
    </div>
  );

  return (
    <div className="space-y-4">
      {items.map((d) => (
        <Card key={d.id} className="p-5 border-border animate-in-up" data-testid={`approval-item-${d.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div>
              <button onClick={() => navigate(`/opportunity/${d.opportunity_id}`)} className="font-semibold hover:text-primary text-left">{d.opportunity?.title}</button>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-muted-foreground">{d.opportunity?.org_name}</span>
                <TrackBadge track={d.opportunity?.track} short />
                {d.suppressed && <Badge variant="outline" className="border-rose-500/40 bg-rose-500/10 text-rose-400 gap-1"><ShieldAlert className="h-3 w-3" /> suppressed</Badge>}
              </div>
            </div>
            <ProvenancePill provenance={d.provenance} aiGenerated={d.ai_generated} />
          </div>
          {d.subject && <div className="text-sm mb-2"><span className="text-muted-foreground">Subject: </span><span className="font-medium">{d.subject}</span></div>}
          <pre className="text-sm whitespace-pre-wrap font-sans rounded-lg bg-muted/30 p-3 max-h-56 overflow-y-auto">{d.edited_content}</pre>
          {d.suppressed && <p className="text-xs text-rose-400 mt-2">This organization's domain is on the suppression list — review before approving.</p>}
          <div className="flex items-center gap-2 mt-4">
            <Button size="sm" onClick={() => setApprove(d)} data-testid={`approve-${kind}-button-${d.id}`} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"><Check className="h-4 w-4" /> Approve {kind}</Button>
            <Button size="sm" variant="outline" onClick={async () => { await api.post(`/drafts/${d.id}/reject`, {}); toast.success("Rejected"); refresh(); }} data-testid={`reject-draft-${d.id}`} className="gap-1.5 text-rose-400 border-rose-500/30 hover:bg-rose-500/10"><X className="h-4 w-4" /> Reject</Button>
            <Button size="sm" variant="ghost" onClick={() => navigate(`/opportunity/${d.opportunity_id}`)} className="gap-1.5 ml-auto"><ExternalLink className="h-4 w-4" /> Open</Button>
          </div>
        </Card>
      ))}

      <ConfirmDialog open={!!approve} onOpenChange={(o) => !o && setApprove(null)} testid={`approve-confirm-${kind}`}
        title={`Approve this ${kind}?`}
        description={kind === "outreach"
          ? "Approval does NOT send the email. You will still have to explicitly send it from the opportunity."
          : "Approval does NOT submit the form. You will still complete the assisted browser handoff yourself."}
        ackLabel={`I have reviewed the content and approve this ${kind} draft.`}
        confirmLabel={`Approve ${kind}`}
        onConfirm={async () => {
          try { await api.post(`/drafts/${approve.id}/approve`, { confirm: true }); toast.success(`${kind} approved`); setApprove(null); refresh(); }
          catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        }} />
    </div>
  );
}
